import moment from 'moment';
import { isEmpty, isNil } from 'lodash';

import { __t, applyTranslations } from './language';
import { isInactive } from './campaign';
import { getDateDiffInDays } from './date';
import { renderPrice } from './helper/price';
import { isOffer } from 'utils/offers/offers';

import type {
  Campaign,
  PaymentAmounts,
  PaymentInfo,
  Payout,
  Subcampaign,
  VariablePayment
} from 'types';

applyTranslations(require('modules/campaigns/components/payments/payouts/common/payouts-common.i18n.json'));

export function getExpectedPayoutDate(releaseDate: string): moment.Moment {
  const endDate = moment(releaseDate);
  const year = endDate.year();
  const month = endDate.month();
  const date = endDate.date();
  const payoutDate = moment({ year, month, date: 25 });

  if (date > 10) { // only next month
    payoutDate.add(1, 'M');
  }

  // COLLAB-14709 FE - Payments : Payout expected 25th ---> 27th for January 2022
  if (payoutDate.year() === 2022 && payoutDate.month() === 0 && payoutDate.date() === 25) {
    payoutDate.add(2, 'd');
  }

  // Similar reasons, but this time we need to set it to 30th May 2023
  if (payoutDate.year() === 2023 && payoutDate.month() === 4 && payoutDate.date() === 25) {
    payoutDate.add(5, 'd');
  }

  return payoutDate;
}

export function getExpectedPayoutDateWithRelationToReleaseStatus(
  dateToCompare: Date,
  campaignEndDatetime: string = '',
  isPaymentReleased: boolean = false
): moment.Moment | undefined {
  if (isEmpty(campaignEndDatetime)) {
    return undefined;
  }

  const mDate = moment(dateToCompare);
  const endDate = mDate.isAfter(campaignEndDatetime) && !isPaymentReleased
    ? mDate
    : moment(campaignEndDatetime);

  return getExpectedPayoutDate(endDate.format('YYYY-MM-DD'));
}

export function canCreatePayouts(campaign: Campaign): boolean {
  // 180 days "grace period" for making instalments after the campaign ends
  // so that instalments can be made for up to 180 days after the campaign end date
  const stillCanCreateInstallments = getDateDiffInDays(new Date(), campaign.endDate) <= 180;

  return !isInactive(campaign) || stillCanCreateInstallments;
}

export function getPayoutDropdownDates(campaignEndDate: string = ''): Array<string> {
  if (isEmpty(campaignEndDate)) {
    return [];
  }

  const dates: Array<string> = [];
  const DAY_OF_MONTH = 10;
  const today = moment();
  const endDate = moment(campaignEndDate).add(8, 'M');

  if (today.date() > DAY_OF_MONTH) {
    today.add(1, 'M');
  }

  const numDatesToShow = endDate.diff(today, 'months');

  for (let i = 0; i <= numDatesToShow; i++) {
    const date = moment(today).date(DAY_OF_MONTH).add(i, 'M');
    dates.push(date.format('YYYY-MM-DD'));
  }

  return dates;
}

export function getAcceptedOfferBudget(subcampaign?: Subcampaign | null): number {
  if (!subcampaign) {
    return 0;
  }

  return subcampaign.offer.budget || 0;
}

export const getTotalVariablePayment = (paymentInfo?: PaymentInfo): number => (paymentInfo ? paymentInfo.amounts.variable : 0);

export const sumVariablePayments = (variablePayments: Array<VariablePayment>): number => variablePayments.reduce((sum, next) => (
  sum + (!next.isDeleted ? next.amount : 0)
), 0);

export const getUnallocatedAmount = (
  payouts: Array<Payout>,
  subcampaign: Subcampaign,
  paymentInfo?: PaymentInfo
): number => {
  const variablePayment = getTotalVariablePayment(paymentInfo);
  let total = 0;

  payouts.forEach(payout => {
    total += payout.amount || 0;
  });

  const offerBudget = getAcceptedOfferBudget(subcampaign) || 0;

  return ((offerBudget + variablePayment) - total) || 0;
};

export function isVeryOldCampaignForPayouts(campaign: Campaign): boolean {
  const finishDate = moment(campaign.endDate);
  const payoutsFeatureReleaseDay = moment('2018-05-31');
  return isInactive(campaign) && finishDate.isBefore(payoutsFeatureReleaseDay);
}

const getPaymentAmounts = (budget: number, variablePayments?: Array<VariablePayment> | null): PaymentAmounts => {
  if (isNil(variablePayments)) {
    return {
      budget,
      agreed: budget,
      variable: 0,
      total: budget
    };
  }

  const variablePaymentAmount = sumVariablePayments(variablePayments);

  return {
    budget,
    agreed: budget,
    variable: variablePaymentAmount,
    total: budget + variablePaymentAmount
  };
};

export const getPaymentInfo = (
  subcampaign: Subcampaign,
  otherArrangement?: boolean,
  variablePayments?: Array<VariablePayment> | null
): PaymentInfo => {
  const paymentLabel = `${__t('payouts_common.labels.total_payout')}:`;
  const budget = isOffer(subcampaign) && subcampaign.offer.budget ? subcampaign.offer.budget : 0;
  const absoluteAmounts = getPaymentAmounts(budget, variablePayments);
  const updatedBudget = absoluteAmounts.budget + absoluteAmounts.variable;
  const offerValue = renderPrice(updatedBudget, { defaultValue: '' });
  const paymentValue = otherArrangement
    ? __t('payouts_common.labels.other')
    : offerValue;

  return {
    label: paymentLabel,
    value: paymentValue,
    amounts: absoluteAmounts
  };
};

export const getVariablePaymentsById = (key: string, id: number, data: Array<VariablePayment>): Array<VariablePayment> => (data || []).filter(item => item[key] === id);

const EMPTY_FIELDS = {
  description: '',
  amount: 0
};

export function generateNewVariablePaymentId() {
  return -Math.floor(Math.random() * Math.floor(1000)); // new variable payment's id's are always negative, existing ones are positive
}

export function isVariablePaymentNew(variablePayment: VariablePayment) {
  return Number(variablePayment.id) < 0; // new variable payment id's are always negative, existing ones are positive
}

export function hasAnyReleasedPayout(payouts?: Array<Payout>): boolean {
  return (payouts || []).some(payout => moment().isAfter(payout.releaseDate));
}

export const populateEmptyFieldsForVariablePayment = (subcampaignId: number, influencerProfileId: number) => ({
  ...EMPTY_FIELDS,
  id: generateNewVariablePaymentId(),
  subcampaignId,
  influencerProfileId,
  isDeleted: false
});
